import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { X } from 'lucide-react'
import { toast } from 'sonner'

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  background: 'var(--field-bg)',
  border: '1px solid var(--border-default)',
  borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)',
  outline: 'none', boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--ink-tertiary)',
  display: 'block', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: '0.06em',
}

export default function LocationModal({
  onClose, onSaved, companyId, existingLocation,
}: {
  onClose: () => void
  onSaved: (saved: any) => void
  companyId: string
  existingLocation?: any
}) {
  const { workspace } = useAuth()
  const isEdit = !!existingLocation
  const [form, setForm] = useState(() => existingLocation ? {
    name: existingLocation.name || '',
    address_line1: existingLocation.address_line1 || '',
    address_line2: existingLocation.address_line2 || '',
    city: existingLocation.city || '',
    state: existingLocation.state || '',
    zip: existingLocation.zip || '',
    country: existingLocation.country || 'US',
    site_contact_name: existingLocation.site_contact_name || '',
    site_contact_phone: existingLocation.site_contact_phone || '',
    site_contact_email: existingLocation.site_contact_email || '',
    access_notes: existingLocation.access_notes || '',
    gate_code: existingLocation.gate_code || '',
    parking_instructions: existingLocation.parking_instructions || '',
    store_number: existingLocation.store_number || '',
    region: existingLocation.region || '',
    district: existingLocation.district || '',
    status: existingLocation.status || 'active',
    notes: existingLocation.notes || '',
  } : {
    name: '', address_line1: '', address_line2: '',
    city: '', state: '', zip: '', country: 'US',
    site_contact_name: '', site_contact_phone: '', site_contact_email: '',
    access_notes: '', gate_code: '', parking_instructions: '',
    store_number: '', region: '', district: '',
    status: 'active', notes: '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Location name is required'); return }
    if (!form.address_line1.trim()) { toast.error('Address is required'); return }
    if (!form.city.trim()) { toast.error('City is required'); return }
    if (!form.state.trim()) { toast.error('State is required'); return }
    if (!form.zip.trim()) { toast.error('ZIP is required'); return }
    setSaving(true)
    const payload = {
      ...form,
      address_line2: form.address_line2 || null,
      site_contact_name: form.site_contact_name || null,
      site_contact_phone: form.site_contact_phone || null,
      site_contact_email: form.site_contact_email || null,
      access_notes: form.access_notes || null,
      gate_code: form.gate_code || null,
      parking_instructions: form.parking_instructions || null,
      store_number: form.store_number || null,
      region: form.region || null,
      district: form.district || null,
      notes: form.notes || null,
    }
    if (isEdit) {
      const { data, error } = await supabase.from('locations').update(payload).eq('id', existingLocation.id).select().single()
      setSaving(false)
      if (error) { toast.error(`Error: ${error.message}`); return }
      toast.success('Location updated')
      onSaved(data); onClose()
    } else {
      const { data, error } = await supabase.from('locations').insert({
        ...payload,
        workspace_id: workspace!.id,
        company_id: companyId,
      }).select().single()
      setSaving(false)
      if (error) { toast.error(`Error: ${error.message}`); return }
      toast.success('Location created')
      onSaved(data); onClose()
    }
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 22, padding: 28, width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{isEdit ? 'Edit location' : 'New location'}</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Location name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Walmart #1234 / Building A" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>

          <div>
            <label style={labelStyle}>Address line 1 *</label>
            <input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="123 Main St" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>

          <div>
            <label style={labelStyle}>Address line 2</label>
            <input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Suite 200" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>City *</label>
              <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="New York" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={labelStyle}>State *</label>
              <input value={form.state} onChange={e => set('state', e.target.value)} placeholder="NY" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={labelStyle}>ZIP *</label>
              <input value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="10001" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={labelStyle}>Country</label>
              <input value={form.country} onChange={e => set('country', e.target.value)} placeholder="US" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Store number</label>
              <input value={form.store_number} onChange={e => set('store_number', e.target.value)} placeholder="#1234" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={labelStyle}>Region</label>
              <input value={form.region} onChange={e => set('region', e.target.value)} placeholder="Northeast" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={labelStyle}>District</label>
              <input value={form.district} onChange={e => set('district', e.target.value)} placeholder="District 5" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' }}>Site contact</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Name</label>
                <input value={form.site_contact_name} onChange={e => set('site_contact_name', e.target.value)} placeholder="Site manager" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input value={form.site_contact_phone} onChange={e => set('site_contact_phone', e.target.value)} placeholder="+1 (555) 000-0000" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={form.site_contact_email} onChange={e => set('site_contact_email', e.target.value)} placeholder="site@example.com" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' }}>Access</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Gate code</label>
                <input value={form.gate_code} onChange={e => set('gate_code', e.target.value)} placeholder="#1234" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Parking instructions</label>
              <textarea value={form.parking_instructions} onChange={e => set('parking_instructions', e.target.value)} placeholder="Lot B, north side" rows={2}
                style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={labelStyle}>Access notes</label>
              <textarea value={form.access_notes} onChange={e => set('access_notes', e.target.value)} placeholder="Loading dock around back, ring buzzer" rows={2}
                style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Anything worth remembering..." rows={2}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save changes' : 'Create location')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
